import React, { useState, useRef, useEffect } from "react";

/**
 * EnhancedTooltip - Modern tooltip with rich content and keyboard shortcuts
 * Features:
 * - Configurable delay
 * - Rich content support (title + description + keyboard shortcut)
 * - Proper positioning
 * - Accessible (role="tooltip")
 */
const EnhancedTooltip = ({
  children,
  content,
  description,
  shortcut,
  position = "top", // top, bottom, left, right
  delay = 500, // ms
  disabled = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const showTooltip = () => {
    if (disabled || !content) return;
    
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
      calculatePosition();
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setVisible(false);
  };

  const handleClick = () => {
    hideTooltip();
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8; // pixels between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case "right":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + gap;
        break;
      default:
        break;
    }

    // Keep tooltip within viewport
    const padding = 10;
    if (left < padding) left = padding;
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }

    setCoords({ top, left });
  };

  // Recalculate position when visible
  useEffect(() => {
    if (visible) {
      calculatePosition();
    }
  }, [visible]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    if (!visible) return;

    const handleResize = () => calculatePosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [visible]);

  const hasRichContent = description || shortcut;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        onClick={handleClick}
        onMouseDown={hideTooltip}
        style={{ display: "inline-block", width: "100%" }}
        aria-describedby={visible ? "enhanced-tooltip" : undefined}
      >
        {children}
      </div>

      {visible && (
        <div
          ref={tooltipRef}
          id="enhanced-tooltip"
          role="tooltip"
          className={`enhanced-tooltip enhanced-tooltip--${position} ${
            hasRichContent ? "enhanced-tooltip--rich" : ""
          }`}
          style={{
            position: "fixed",
            top: `${coords.top}px`,
            left: `${coords.left}px`,
          }}
        >
          <div className="enhanced-tooltip-content">
            {hasRichContent ? (
              <div className="enhanced-tooltip-rich">
                <div className="enhanced-tooltip-title">{content}</div>
                {description && (
                  <div className="enhanced-tooltip-description">{description}</div>
                )}
                {shortcut && (
                  <div className="enhanced-tooltip-shortcut">
                    {Array.isArray(shortcut) ? (
                      shortcut.map((key, i) => (
                        <kbd key={i} className="enhanced-tooltip-kbd">
                          {key}
                        </kbd>
                      ))
                    ) : (
                      <kbd className="enhanced-tooltip-kbd">{shortcut}</kbd>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <span className="enhanced-tooltip-simple">{content}</span>
            )}
          </div>
          <div className={`enhanced-tooltip-arrow enhanced-tooltip-arrow--${position}`} />
        </div>
      )}
    </>
  );
};

export default EnhancedTooltip;
